import { Injectable } from '@angular/core';
import {
    AccountProviderControllerService,
    AccountProviderResponse,
    CreateAccountProviderRequest,
    ExternalIdResponse,
    UpdateAccountProviderExternalIdRequest,
} from '@app/core/generated/openapi';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class AccountProviderService {
    constructor(
        private service: AccountProviderControllerService,
        private formBuilder: FormBuilder,
    ) {}

    // @HttpCache('accountProviders', 120000)
    findAllAccountProviders(): Observable<Array<AccountProviderResponse>> {
        return this.service.findAllProvidersForAccount();
    }

    findAccountProviderById(id: number): Observable<AccountProviderResponse> {
        return this.service.findProviderById(id);
    }

    // @InvalidatesCacheTag('accountProviders')
    addProvider(
        createAccountProviderRequest: CreateAccountProviderRequest,
    ): Observable<AccountProviderResponse> {
        // return this.service.createAccountProvider(createAccountProviderRequest);
        return this.findAccountProviderById(1);
    }

    getCreateForm(): FormGroup {
        return this.formBuilder.group({
            firstName: new FormControl('', {
                validators: [Validators.required, Validators.maxLength(50)],
                updateOn: 'change',
            }),
            lastName: new FormControl('', {
                validators: [Validators.required, Validators.maxLength(100)],
                updateOn: 'change',
            }),
            profCredentials: new FormControl(),
            phoneNumber: new FormControl('', {
                validators: [Validators.maxLength(35)],
                updateOn: 'change',
            }),
            email: new FormControl('', {
                validators: [Validators.maxLength(100), Validators.email],
                updateOn: 'change',
            }),
            npi: new FormControl('', {
                validators: [
                    Validators.minLength(10),
                    Validators.maxLength(10),
                ],
                updateOn: 'change',
            }),
            noteText: new FormControl('', {
                validators: [Validators.maxLength(2000)],
                updateOn: 'change',
            }),
        });
    }

    getUpdateForm(accountProvider: AccountProviderResponse): FormGroup {
        return this.formBuilder.group({
            id: new FormControl(accountProvider.id),
            firstName: new FormControl(accountProvider.firstName, {
                validators: [Validators.required, Validators.maxLength(50)],
                updateOn: 'change',
            }),
            lastName: new FormControl(accountProvider.lastName, {
                validators: [Validators.required, Validators.maxLength(100)],
                updateOn: 'change',
            }),
            profCredentials: new FormControl(accountProvider.profCredentials),
            phoneNumber: new FormControl(accountProvider.phoneNumber, {
                validators: [Validators.maxLength(35)],
                updateOn: 'change',
            }),
            email: new FormControl(accountProvider.email, {
                validators: [Validators.maxLength(100), Validators.email],
                updateOn: 'change',
            }),
            npi: new FormControl(accountProvider.npi, {
                validators: [
                    Validators.minLength(10),
                    Validators.maxLength(10),
                ],
                updateOn: 'change',
            }),
            noteText: new FormControl(accountProvider.noteText, {
                validators: [Validators.maxLength(2000)],
                updateOn: 'change',
            }),
        });
    }

    // @InvalidatesCacheTag('accountProviders')
    updateProvider(formGroup: FormGroup) {
        return this.service.updateAccountProvider(formGroup.getRawValue());
    }

    // @InvalidatesCacheTag('accountProviders')
    createProvider(createForm: FormGroup) {
        let request = createForm.getRawValue();
        return this.service.createAccountProvider(request);
    }

    // @InvalidatesCacheTag('accountProviders')
    updateStatus(
        accountProviderId: number,
        status: AccountProviderResponse.StatusEnum,
    ) {
        if (status === AccountProviderResponse.StatusEnum.Inactive) {
            return this.service.deactivateAccountProvider(accountProviderId);
        } else {
            return this.service.activateAccountProvider(accountProviderId);
        }
    }

    // @InvalidatesCacheTag('accountProviders')
    updateProviderExternalId(
        formGroup: FormGroup,
    ): Observable<ExternalIdResponse> {
        return this.service.updateAccountProviderExternalId(
            formGroup.getRawValue(),
        );
    }

    // @InvalidatesCacheTag('accountProviders')
    addProviderExternalId(
        formGroup: FormGroup,
    ): Observable<AccountProviderResponse> {
        return this.service.addExternalIdToAccountProvider(
            formGroup.getRawValue(),
        );
    }

    // @InvalidatesCacheTag('accountProviders')
    updateExternalIdStatus(
        extIdReq: UpdateAccountProviderExternalIdRequest,
        status: ExternalIdResponse.StatusEnum,
    ): Observable<AccountProviderResponse> {
        if (status === ExternalIdResponse.StatusEnum.Active) {
            return this.service.inactivateAccountProviderExternalId(extIdReq);
        } else {
            return this.service.activateAccountProviderExternalId(extIdReq);
        }
    }

    // @InvalidatesCacheTag('accountProviders')
    deleteProviderExternalId(
        formgroup: FormGroup,
    ): Observable<AccountProviderResponse> {
        return this.service.deleteExternalIdFromAccountProvider(
            formgroup.getRawValue(),
        );
    }
}
